// ─── LISTENERS BRIDGE — DOM event listeners residui post-split ──────────────
//
// Side-effect module che registra DOM event listeners orfani residui dallo
// <script> classic inline post-split ADR-0005:
//
// 1. **Esc key handler**: chiude modal welcome/consent/announcement su Escape.
//    Legal modal è BLOCCANTE (Esc preventDefault, no close).
//
// 2. **loginEmail Enter**: invio magic link al pressione Enter sul campo email.
//
// 3. **Pointer-tracker IIFE**: glow follow-pointer su card (mouse + touch).
//    Espone `window._attachPointerFx` per re-bind su elementi dinamici.
//
// 4. **Topbar height sync IIFE**: sync CSS var `--topbar-h` con altezza reale
//    `.hdr` (usata da .plan-drawer e .chat-panel position:fixed top).
//
// Estratto da `index.html` <script> classic inline in PR refactor/bkl016-pr4
// (2026-05-21, completamento ADR-0005 split). Sister BKL-067 (cleanup window.*
// bridge per i listener restanti).
//
// Dipendenze esterne (esposte su window via main.js bridge):
// - `window.closeWelcome` (ui-onboarding.js)
// - `window.sendMagicLink` (ui-auth.js)
// - `window.initHomeChartSwitcher` (ui-home.js)

// ─── 1. Esc key handler — modal dismiss su Escape ───────────────────────────
//
// Esc: welcome → chiude e marca flag (come "Salta"); consent → chiude SENZA
// scelta (riapparirà al prossimo login; non assumiamo "no" se l'utente
// preme un tasto per sbaglio).
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  // Legal modal è BLOCCANTE: Esc non lo chiude. L'unica via di uscita è
  // accettare oppure cliccare "Non accetto · esci" che fa logout.
  const legal = document.getElementById('legalModal');
  if (legal && legal.classList.contains('show')) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  const welcome = document.getElementById('welcomeModal');
  if (welcome && welcome.classList.contains('show')) {
    e.preventDefault();
    if (typeof window.closeWelcome === 'function') {
      window.closeWelcome();
    }
    return;
  }
  const consent = document.getElementById('consentModal');
  if (consent && consent.classList.contains('show')) {
    e.preventDefault();
    consent.classList.remove('show');
    // Niente flag write — riapparirà al prossimo login finché l'utente non sceglie
    return;
  }
  const announcement = document.getElementById('announcementModal');
  if (announcement && announcement.classList.contains('show')) {
    e.preventDefault();
    announcement.classList.remove('show');
    // Niente flag write — riapparirà al prossimo login. Il dismiss permanente
    // richiede click esplicito su "OK, ho capito".
  }
});

// ─── 2. loginEmail Enter → magic link ───────────────────────────────────────
//
// Enter sul campo email del login → invia magic link (sendMagicLink esportata
// da ui-auth.js + esposta su window).
// Guard `if` su element + function presence: ui-auth bridge potrebbe non essere
// ancora caricato in alcuni edge case (es. test/preview senza Vite).
(function bindLoginEmailEnter() {
  const el = document.getElementById('loginEmail');
  if (!el) return;
  el.addEventListener('keydown', e => {
    if (e.key !== 'Enter') return;
    if (typeof window.sendMagicLink === 'function') {
      window.sendMagicLink();
    } else {
      console.warn('[listeners-bridge] window.sendMagicLink not exposed — ui-auth bridge missing');
    }
  });
})();

// ─── 3. Pointer-tracker — glow follow-pointer su card ───────────────────────
//
// Interactive spotlight effect (mouse + touch) su elementi `.plan-tile`,
// `.home-day-card`, `.plan-drawer-tab`, `.home-widget`. Aggiorna CSS vars
// `--mx`/`--my` con coordinate relative all'elemento.
//
// Espone `window._attachPointerFx(el)` per re-bind su elementi creati
// dinamicamente (es. nuovi `.home-day-card` post-rerender).
//
// Skip se utente preferisce `prefers-reduced-motion`.
(function pointerTrackerIIFE() {
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;
  const setPos = (el, x, y) => {
    const r = el.getBoundingClientRect();
    if (!r.width) return;
    el.style.setProperty('--mx', (x - r.left) + 'px');
    el.style.setProperty('--my', (y - r.top) + 'px');
  };
  const attach = el => {
    if (el.dataset.fxBound) return;
    el.dataset.fxBound = '1';
    el.addEventListener('mouseenter', e => setPos(el, e.clientX, e.clientY), {passive:true});
    el.addEventListener('mousemove',  e => setPos(el, e.clientX, e.clientY), {passive:true});
    el.addEventListener('touchstart', e => {
      const t = e.touches[0]; if (!t) return;
      el.classList.add('is-touched');
      setPos(el, t.clientX, t.clientY);
    }, {passive:true});
    el.addEventListener('touchmove', e => {
      const t = e.touches[0]; if (!t) return;
      setPos(el, t.clientX, t.clientY);
    }, {passive:true});
    const release = () => el.classList.remove('is-touched');
    el.addEventListener('touchend',    release, {passive:true});
    el.addEventListener('touchcancel', release, {passive:true});
  };
  document.querySelectorAll('.plan-tile, .home-day-card, .plan-drawer-tab, .home-widget').forEach(attach);
  window._attachPointerFx = attach;
  // Init home chart metric switcher (dropdown — handler bound async via main.js
  // ESM bridge). setTimeout 0 garantisce che window.initHomeChartSwitcher sia
  // esposto prima della chiamata.
  setTimeout(() => {
    if (typeof window.initHomeChartSwitcher === 'function') {
      window.initHomeChartSwitcher();
    }
  }, 0);
})();

// ─── 4. Topbar height sync — CSS var --topbar-h ─────────────────────────────
//
// Sync `--topbar-h` CSS var con altezza reale `.hdr`.
// `.plan-drawer` e `.chat-panel` sono position:fixed con top:var(--topbar-h).
// Fallback 56px (default CSS) se `.hdr` non misurabile (es. #mainApp display:none al boot).
// ResizeObserver triggera al login (mainApp da hidden a visible) e su
// resize/font-load. Risolve dislivello visivo tra topbar e drawer.
(function topbarHeightSyncIIFE() {
  const hdr = document.querySelector('.hdr');
  if (!hdr) return;
  let _rafPending = false;
  const sync = () => {
    if (_rafPending) return;
    _rafPending = true;
    requestAnimationFrame(() => {
      _rafPending = false;
      const h = hdr.offsetHeight;
      if (h > 0) document.documentElement.style.setProperty('--topbar-h', h + 'px');
    });
  };
  if (window.ResizeObserver) {
    new ResizeObserver(sync).observe(hdr);
  }
  window.addEventListener('resize', sync, {passive:true});
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);
  sync();
})();
